/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { YtjCompaniesResponse } from '../models/ytj-companies-response';


/**
 * Finnish companies in YTJ, the Finnish business information system,
 * searched by name.
 */
@Injectable({
  providedIn: 'root',
})
export class YtjService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation searchYtjCompanies
   */
  static readonly SearchYtjCompaniesPath = '/ytj-companies';

  /**
   * Search Finnish companies in YTJ by name.
   *
   * Searches YTJ, the Finnish business information system, for the
   * companies whose name holds `name`, as the extraction review form's
   * end-customer search does for a buyer not yet among our end customers.
   * YTJ matches current, previous, parallel and auxiliary names alike.
   * Answers at most 10 companies, in YTJ's order, each with its current
   * name, its business ID and its registered address: its postal address,
   * or its street address when it has none. A company that has ended
   * (YTJ gives it a dissolution date) is left out: it is no buyer to
   * factor an invoice to.
   *
   * cool-api asks YTJ's public open-data API directly, so the answer comes
   * while the person types. When YTJ fails or does not answer within a few
   * seconds, the search answers a `502` with `error_code`
   * `ytj_unavailable`, distinct from a `200` without companies, so the form
   * can say that YTJ could not be searched while our end customers still
   * show.
   *
   * Needs only an authenticated caller, as `listEndCustomers` does.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchYtjCompanies()` instead.
   *
   * This method doesn't expect any request body.
   */
  searchYtjCompanies$Response(params: {

    /**
     * Part of the company&#x27;s name, matched by YTJ. The spaces around it are ignored; a blank one is refused with a &#x60;400&#x60;.
     */
    name: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<YtjCompaniesResponse>> {

    const rb = new RequestBuilder(this.rootUrl, YtjService.SearchYtjCompaniesPath, 'get');
    if (params) {
      rb.query('name', params.name, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<YtjCompaniesResponse>;
      })
    );
  }

  /**
   * Search Finnish companies in YTJ by name.
   *
   * Searches YTJ, the Finnish business information system, for the
   * companies whose name holds `name`, as the extraction review form's
   * end-customer search does for a buyer not yet among our end customers.
   * YTJ matches current, previous, parallel and auxiliary names alike.
   * Answers at most 10 companies, in YTJ's order, each with its current
   * name, its business ID and its registered address: its postal address,
   * or its street address when it has none. A company that has ended
   * (YTJ gives it a dissolution date) is left out: it is no buyer to
   * factor an invoice to.
   *
   * cool-api asks YTJ's public open-data API directly, so the answer comes
   * while the person types. When YTJ fails or does not answer within a few
   * seconds, the search answers a `502` with `error_code`
   * `ytj_unavailable`, distinct from a `200` without companies, so the form
   * can say that YTJ could not be searched while our end customers still
   * show.
   *
   * Needs only an authenticated caller, as `listEndCustomers` does.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `searchYtjCompanies$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  searchYtjCompanies(params: {

    /**
     * Part of the company&#x27;s name, matched by YTJ. The spaces around it are ignored; a blank one is refused with a &#x60;400&#x60;.
     */
    name: string;
    context?: HttpContext
  }
): Observable<YtjCompaniesResponse> {

    return this.searchYtjCompanies$Response(params).pipe(
      map((r: StrictHttpResponse<YtjCompaniesResponse>) => r.body as YtjCompaniesResponse)
    );
  }

}
